'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowUp, Mic, Square } from 'lucide-react';
import { PulsingDots } from '@/shared/ui/PulsingDots';
import { useAssistantStore } from '../model/assistant.store';
import type { VoiceState } from '../hooks/use-voice-input';

/** Grows with the text up to five lines, then scrolls (ADR-001 client spec). */
const MAX_INPUT_HEIGHT = 120;

export interface AssistantComposerProps {
  disabled: boolean;
  onSend: (text: string) => void;
  voice: {
    isSupported: boolean;
    state: VoiceState;
    startedAt: number | null;
    start: () => void;
    stop: () => void;
  };
}

export function AssistantComposer({ disabled, onSend, voice }: AssistantComposerProps) {
  const t = useTranslations('assistant');
  const draft = useAssistantStore((s) => s.draft);
  const setDraft = useAssistantStore((s) => s.setDraft);

  const send = () => {
    if (disabled || !draft.trim()) return;
    onSend(draft);
    setDraft('');
  };

  const recording = voice.state === 'recording';
  const transcribing = voice.state === 'transcribing';

  return (
    <div className="space-y-2">
      {(recording || transcribing) && (
        <div className="flex items-center gap-2 px-1 text-card-meta text-muted-foreground">
          <PulsingDots size="sm" />
          <span>{recording ? t('listening') : t('transcribing')}</span>
          {recording && voice.startedAt !== null && <Elapsed since={voice.startedAt} />}
        </div>
      )}
      <div className="flex items-end gap-2 rounded-[26px] border border-foreground/55 bg-background ps-3 pe-1 py-1 transition-colors focus-within:border-primary">
        {/* Auto-grow without measuring: an invisible mirror of the text (or of the
            placeholder while empty, so a placeholder that wraps gets its lines
            instead of a scrollbar) sizes the grid cell, the textarea fills it and
            scrolls only past the five-line cap. */}
        <div className="grid min-w-0 flex-1">
          <div
            aria-hidden
            className="invisible col-start-1 row-start-1 overflow-hidden whitespace-pre-wrap break-words py-2.5 text-composer-input"
            style={{ maxHeight: MAX_INPUT_HEIGHT }}
          >
            {`${draft || t('placeholder')}\n`}
          </div>
          <textarea
            rows={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={t('placeholder')}
            aria-label={t('placeholder')}
            className="col-start-1 row-start-1 h-full min-w-0 resize-none bg-transparent py-2.5 text-composer-input text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        {voice.isSupported && (
          <button
            type="button"
            onClick={recording ? voice.stop : voice.start}
            disabled={transcribing}
            aria-label={recording ? t('voiceStop') : t('voiceStart')}
            // A 44 px tap target, 8 px from the send button's (Apple
            // `AssistantComposerView`); recording shows a 36 px red circle in it.
            className="flex size-11 flex-shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            {recording ? (
              <span className="flex size-9 items-center justify-center rounded-full bg-destructive text-white">
                <Square className="size-4 fill-current" />
              </span>
            ) : (
              <Mic className="size-[22px]" />
            )}
          </button>
        )}
        <button
          type="button"
          onClick={send}
          disabled={disabled || !draft.trim()}
          aria-label={t('send')}
          className="group flex size-11 flex-shrink-0 items-center justify-center"
        >
          <span className="flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors group-hover:bg-primary/90 group-disabled:bg-button-disabled group-disabled:text-white">
            <ArrowUp className="size-[18px]" strokeWidth={2.25} />
          </span>
        </button>
      </div>
    </div>
  );
}

function Elapsed({ since }: { since: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.floor((now - since) / 1000));
  return (
    <span className="tabular-nums">
      {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
    </span>
  );
}
